<script setup lang="ts">
import BaseButton from "@/components/Base/Button.vue";
import type { BootstrapIconName } from "@/types/bootstrap-icons";
import type {
  ButtonKind,
  ButtonSize,
  ButtonTertiaryColor,
} from "@/types/components/Button.types";
import { Dropdown } from "bootstrap";
import type { Component } from "vue";

defineOptions({ name: "BaseDropdown" });

export type AutoCloseOptions = boolean | "inside" | "outside";

export type Props = {
  id: string;
  buttonClass?: string;
  buttonAriaLabel: string;
  buttonAttrs?: Record<string, unknown>;
  dropdownMenuClass?: string;
  inverted?: boolean;
  disabled?: boolean;
  autoClose?: AutoCloseOptions;
  dropup?: boolean;
  /** Below `md`, cap the menu to the viewport and scroll it instead of overflowing the screen. */
  scrollableOnMobile?: boolean;
  /** ARIA role for the list, e.g. `menu` when the items are `menuitemradio`s. */
  menuRole?: string;
  kind?: ButtonKind;
  tertiaryColor?: ButtonTertiaryColor;
  size?: ButtonSize;
  noPadding?: boolean;
  iconName?: BootstrapIconName;
  iconSvg?: Component;
  iconOnly?: boolean;
  iconOnlyMobile?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  dropdownMenuClass: "dropdown-menu-end",
  autoClose: true,
  kind: "tertiary",
  tertiaryColor: "main",
});

const emit = defineEmits(["show", "hide"]);

const dropdownRef = ref<HTMLElement | null>(null);
let dropdown: Dropdown | null = null;

onMounted(() => {
  if (dropdownRef.value) {
    const options = {
      autoClose: props.autoClose,
    };
    dropdown = new Dropdown(dropdownRef.value, options);
    dropdownRef.value.addEventListener("show.bs.dropdown", function () {
      emit("show");
    });
    dropdownRef.value.addEventListener("hide.bs.dropdown", function (event) {
      emit("hide");
      closedByItem = isItemClick(event);
    });
    dropdownRef.value.addEventListener("hidden.bs.dropdown", restoreFocus);
  }
});

// Bootstrap only hands focus back to the toggle on Escape. Choosing an item
// (by click or Enter) hides the focused item instead, which drops focus to the
// page body; send it back to the toggle so keyboard users keep their place.
let closedByItem = false;

const isItemClick = (event: Event): boolean => {
  const target = (event as Event & { clickEvent?: Event }).clickEvent?.target;
  return (
    target instanceof Node &&
    !!dropdownRef.value?.querySelector(".dropdown-menu")?.contains(target)
  );
};

const restoreFocus = (): void => {
  if (!closedByItem) return;
  closedByItem = false;
  const active = document.activeElement;
  const lostFocus =
    !active ||
    active === document.body ||
    !!dropdownRef.value?.querySelector(".dropdown-menu")?.contains(active);
  if (lostFocus) {
    dropdownRef.value
      ?.querySelector<HTMLElement>("[data-bs-toggle='dropdown']")
      ?.focus();
  }
};

onBeforeUnmount(() => {
  if (dropdown) {
    dropdown.dispose();
  }
});
</script>

<template>
  <div ref="dropdownRef" :class="dropup ? 'dropup' : 'dropdown'">
    <BaseButton
      :id="`dropdown-button-${id}`"
      :class="buttonClass"
      :disabled="disabled"
      :kind="kind"
      :tertiary-color="tertiaryColor"
      :size="size"
      :aria-label="buttonAriaLabel"
      :no-padding="noPadding"
      :icon-name="iconName"
      :icon-svg="iconSvg"
      :icon-only="iconOnly"
      :icon-only-mobile="iconOnlyMobile"
      data-bs-toggle="dropdown"
      aria-expanded="false"
      v-bind="buttonAttrs"
    >
      <slot name="button"></slot>
    </BaseButton>
    <ul
      class="dropdown-menu"
      :role="menuRole"
      :class="[
        dropdownMenuClass,
        { 'dropdown-menu--scrollable-mobile': scrollableOnMobile },
      ]"
      :aria-labelledby="`dropdown-button-${id}`"
    >
      <slot name="default"></slot>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
// A menu that unfolds inline on phones (see BaseDropdownNested) can grow
// taller than the screen. Cap it so the top of the list stays reachable by
// scrolling instead of being pushed off-screen.
@include media-breakpoint-down(md) {
  .dropdown-menu--scrollable-mobile {
    max-height: calc(100vh - 8rem);
    overflow-y: auto;
  }
}
</style>
